import React, { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { useUserSearch, MentionUser } from '@/hooks/use-mentions';
import { getCurrentMention, insertMention, parseTextWithMentions } from '@/utils/mentions';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

interface MentionInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  textareaClassName?: string;
  maxLength?: number;
  rows?: number;
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
}

export const MentionInput = React.forwardRef<HTMLDivElement, MentionInputProps>(
  ({ value, onChange, placeholder, className, textareaClassName, maxLength, rows = 3, onKeyDown }, ref) => {
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [currentQuery, setCurrentQuery] = useState('');
    const [cursorPosition, setCursorPosition] = useState(0);
    const editableRef = useRef<HTMLDivElement | null>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);
    
    // Search for users based on current mention query
    const { data: users = [], isLoading } = useUserSearch(currentQuery, showSuggestions);

    // Get plain text from contenteditable div
    const getPlainText = (element: HTMLDivElement): string => {
      return element.textContent || '';
    };

    // Get cursor position in plain text
    const getCursorPosition = (): number => {
      const selection = window.getSelection();
      if (!selection || !editableRef.current) return 0;
      
      const range = selection.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(editableRef.current);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      
      return preCaretRange.toString().length;
    };

    // Set cursor position in contenteditable
    const setCursorPos = (pos: number) => {
      if (!editableRef.current) return;
      
      const selection = window.getSelection();
      const range = document.createRange();
      
      let charCount = 0;
      let foundPosition = false;
      
      const traverseNodes = (node: Node) => {
        if (foundPosition) return;
        
        if (node.nodeType === Node.TEXT_NODE) {
          const textLength = node.textContent?.length || 0;
          if (charCount + textLength >= pos) {
            range.setStart(node, pos - charCount);
            range.collapse(true);
            foundPosition = true;
            return;
          }
          charCount += textLength;
        } else {
          for (let i = 0; i < node.childNodes.length; i++) {
            traverseNodes(node.childNodes[i]);
            if (foundPosition) return;
          }
        }
      };
      
      traverseNodes(editableRef.current);
      
      if (foundPosition) {
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    };

    const handleInput = () => {
      if (!editableRef.current) return;
      
      let plainText = getPlainText(editableRef.current);
      const cursorPos = getCursorPosition();
      
      // Enforce maxLength by truncating
      if (maxLength && plainText.length > maxLength) {
        plainText = plainText.slice(0, maxLength);
        // Update the DOM immediately with truncated text
        onChange(plainText);
        return;
      }
      
      onChange(plainText);
      setCursorPosition(cursorPos);
      
      // Check if user is typing a mention
      const mention = getCurrentMention(plainText, cursorPos);
      
      if (mention) {
        setCurrentQuery(mention.username);
        setShowSuggestions(true);
        setSelectedIndex(0);
      } else {
        setShowSuggestions(false);
        setCurrentQuery('');
      }
    };

    const selectUser = (user: MentionUser) => {
      if (!editableRef.current) return;
      
      const result = insertMention(value, cursorPosition, user.username);
      onChange(result.newText);
      
      // Update cursor position after React re-renders
      setTimeout(() => {
        setCursorPos(result.newCursorPosition);
        editableRef.current?.focus();
      }, 0);
      
      setShowSuggestions(false);
      setCurrentQuery('');
      setSelectedIndex(0);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (showSuggestions && users.length > 0) {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setSelectedIndex((prev) => (prev + 1) % users.length);
            break;
          case 'ArrowUp':
            e.preventDefault();
            setSelectedIndex((prev) => (prev - 1 + users.length) % users.length);
            break;
          case 'Enter':
            if (showSuggestions) {
              e.preventDefault();
              selectUser(users[selectedIndex]);
            }
            break;
          case 'Escape':
            e.preventDefault();
            setShowSuggestions(false);
            setCurrentQuery('');
            break;
          default:
            break;
        }
      }
      
      // Call parent onKeyDown if provided
      onKeyDown?.(e);
    };

    // Update rendered content when value changes
    useEffect(() => {
      if (!editableRef.current) return;
      
      const currentText = editableRef.current.textContent || '';
      if (currentText === value) return;
      
      // Save cursor position before updating
      const selection = window.getSelection();
      let cursorPos = 0;
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(editableRef.current);
        preCaretRange.setEnd(range.endContainer, range.endOffset);
        cursorPos = preCaretRange.toString().length;
      }
      
      // Clear and rebuild content with styled mentions
      editableRef.current.innerHTML = '';
      const segments = parseTextWithMentions(value);
      
      segments.forEach((segment, index) => {
        if (segment.isMention) {
          const span = document.createElement('span');
          span.style.fontWeight = 'bold';
          span.style.color = '#c084fc';
          span.textContent = segment.text;
          // Prevent editing inside mention span
          span.contentEditable = 'false';
          editableRef.current?.appendChild(span);
          
          // Add a zero-width space after mention to ensure cursor can be placed outside
          const nextSegment = segments[index + 1];
          if (!nextSegment || nextSegment.text.length === 0) {
            const spacer = document.createElement('span');
            spacer.style.color = 'rgb(255, 255, 255)';
            spacer.style.fontWeight = 'normal';
            spacer.textContent = '\u200B'; // Zero-width space
            editableRef.current?.appendChild(spacer);
          }
        } else {
          // Wrap regular text in span with explicit white color to prevent style inheritance
          const span = document.createElement('span');
          span.style.color = 'rgb(255, 255, 255)'; // Explicit white
          span.style.fontWeight = 'normal';
          span.textContent = segment.text;
          editableRef.current?.appendChild(span);
        }
      });
      
      // Restore cursor position
      setTimeout(() => {
        if (!editableRef.current) return;
        const sel = window.getSelection();
        const range = document.createRange();
        
        let charCount = 0;
        let found = false;
        let lastNode: Node | null = null;
        
        const traverse = (node: Node) => {
          if (found) return;
          
          if (node.nodeType === Node.TEXT_NODE) {
            const len = node.textContent?.length || 0;
            if (charCount + len >= cursorPos) {
              // Found the target position
              range.setStart(node, Math.min(cursorPos - charCount, len));
              range.collapse(true);
              found = true;
              return;
            }
            charCount += len;
            lastNode = node;
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            // Traverse element's children
            for (let i = 0; i < node.childNodes.length; i++) {
              traverse(node.childNodes[i]);
              if (found) return;
            }
            // If this is a span element, remember it as last node
            if (!found && node.childNodes.length > 0) {
              lastNode = node;
            }
          }
        };
        
        traverse(editableRef.current);
        
        // If position found, set the range
        if (found) {
          sel?.removeAllRanges();
          sel?.addRange(range);
        } else if (lastNode) {
          // Fallback: place cursor at the end of last node
          if (lastNode.nodeType === Node.TEXT_NODE) {
            range.setStart(lastNode, lastNode.textContent?.length || 0);
          } else {
            range.setStartAfter(lastNode);
          }
          range.collapse(true);
          sel?.removeAllRanges();
          sel?.addRange(range);
        }
      }, 0);
    }, [value]);

    // Scroll selected item into view
    useEffect(() => {
      if (suggestionsRef.current) {
        const selectedElement = suggestionsRef.current.children[selectedIndex] as HTMLElement;
        if (selectedElement) {
          selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }
    }, [selectedIndex]);

    // Handle clicks outside to close suggestions
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          suggestionsRef.current &&
          !suggestionsRef.current.contains(event.target as Node) &&
          editableRef.current &&
          !editableRef.current.contains(event.target as Node)
        ) {
          setShowSuggestions(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
      <div className={cn('relative w-full', className)}>
        <div
          ref={(node) => {
            editableRef.current = node;
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          data-placeholder={placeholder}
          role="textbox"
          aria-multiline="true"
          aria-label={placeholder || 'Text input'}
          className={cn(
            'relative w-full resize-none rounded-md border border-input px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 whitespace-pre-wrap break-words',
            textareaClassName,
            !value && 'empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground empty:before:pointer-events-none'
          )}
          style={{
            minHeight: `${rows * 1.5}em`,
            maxHeight: '300px',
            overflowY: 'auto'
          }}
        />
        
        {showSuggestions && (
          <div
            ref={suggestionsRef}
            className="absolute z-[100] w-full mt-1 bg-slate-900 border border-slate-700 rounded-md shadow-xl max-h-60 overflow-y-auto"
          >
            {isLoading ? (
              <div className="px-4 py-3 text-sm text-gray-400">
                Searching users...
              </div>
            ) : users.length > 0 ? (
              users.map((user, index) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => selectUser(user)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-slate-800 transition-colors',
                    index === selectedIndex && 'bg-slate-800'
                  )}
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user.profile_image_url || undefined} />
                    <AvatarFallback className="text-xs bg-slate-700">
                      {user.username.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate text-white">
                      @{user.username}
                    </div>
                    {user.name && (
                      <div className="text-xs text-gray-400 truncate">
                        {user.name}
                      </div>
                    )}
                  </div>
                </button>
              ))
            ) : (
              <div className="px-4 py-3 text-sm text-gray-400">
                No users found matching "@{currentQuery}"
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

MentionInput.displayName = 'MentionInput';
